<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    EmailTemplatesService,
    FormsService,
    QuestionnairesService,
    type CustomerEmailTemplate as EmailTemplate,
    type CustomerUser as User,
    type CustomerQuestionnaire,
    type CustomerForm
  } from '@/types/generated'
  import { onMounted, ref, computed, watch } from 'vue'
  import { Button } from '@/components/ui/button'
  import * as yup from 'yup'
  import { useRouter } from 'vue-router'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import { SlashIcon, EyeIcon } from '@heroicons/vue/24/outline'
  import { useUserStore } from '@/shared/stores/userStore'
  import DocumentTemplateSection from '@/components/app/email/documentTemplateSection.vue'
  import QuestionnaireTemplateSection from '@/components/app/email/questionnaireTemplateSection.vue'
  import { genericErrorToast, saveSuccessToast } from '@/lib/toasts'
  import EmailPreviewModal from '@/components/app/email/emailPreviewModal.vue'
  import { debounce } from '@/lib/timing'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import { storeToRefs } from 'pinia'

  const router = useRouter()
  const template = ref<EmailTemplate>()
  const showPreviewModal = ref(false)
  const allForms = ref<CustomerForm[]>([])
  const allQuestionnaires = ref<CustomerQuestionnaire[]>([])
  const isLoading = ref(true)
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)
  const { fetchUser } = userStore

  // Create local computed for lawFieldOptions since the store might not have it
  const lawFieldOptions = computed(() => {
    const lawFields = user.value?.org.law_fields || []
    return (lawFields as string[]).map((lawField) => ({
      value: lawField,
      title: lawField
    }))
  })

  const formsPage = ref(1)
  const formsPerPage = ref(10)
  const totalForms = ref(0)
  const selectedFormIds = ref<string[]>([])

  const questionnairesPage = ref(1)
  const questionnairesPerPage = ref(10)
  const totalQuestionnaires = ref(0)
  const selectedQuestionnaireUuids = ref<string[]>([])

  onMounted(async () => {
    isLoading.value = true
    try {
      const fetching = [fetchTemplate(), fetchAllForms(), fetchAllQuestionnaires(), fetchUser()]
      await Promise.all(fetching)
    } finally {
      isLoading.value = false
    }
  })

  const fetchTemplate = async () => {
    template.value = (await EmailTemplatesService.emailTemplatesRetrieve({
      uuid: router.currentRoute.value.params.uuid.toString()
    })) as EmailTemplate
    selectedFormIds.value = template.value?.forms.map((form) => form.id) || []
    selectedQuestionnaireUuids.value = template.value?.questionnaires.map((q) => q.uuid) || []
  }

  const fetchAllForms = async (searchQuery?: string) => {
    const response = await FormsService.formsList({
      tag: 'MASTER',
      limit: formsPerPage.value,
      offset: (formsPage.value - 1) * formsPerPage.value,
      search: searchQuery
    })
    allForms.value = response.results as CustomerForm[]
    totalForms.value = response.count
  }

  const fetchAllQuestionnaires = async (searchQuery?: string) => {
    const response = await QuestionnairesService.questionnairesList({
      limit: questionnairesPerPage.value,
      offset: (questionnairesPage.value - 1) * questionnairesPerPage.value,
      search: searchQuery
    })
    allQuestionnaires.value = response.results as CustomerQuestionnaire[]
    totalQuestionnaires.value = response.count
  }

  const handleFormsPageChange = async (page: number) => {
    formsPage.value = page
    await fetchAllForms()
  }

  const handleFormsSearch = async (query: string) => {
    formsPage.value = 1
    await fetchAllForms(query)
  }

  const handleQuestionnairesPageChange = async (page: number) => {
    questionnairesPage.value = page
    await fetchAllQuestionnaires()
  }

  const handleQuestionnairesSearch = async (query: string) => {
    questionnairesPage.value = 1
    await fetchAllQuestionnaires(query)
  }

  const typeOptions = [
    { value: 'create', title: 'Aufnahme' },
    { value: 'reject', title: 'Ablehnung' }
  ]

  const handleSelectedFormsUpdate = (newSelectedFormIds: string[]) => {
    selectedFormIds.value = newSelectedFormIds
  }

  const handleSelectedQuestionnairesUpdate = (newSelectedQuestionnaireIds: string[]) => {
    selectedQuestionnaireUuids.value = newSelectedQuestionnaireIds
  }

  const handleFormSave = async (values: any) => {
    if (!template.value) return

    await EmailTemplatesService.emailTemplatesPartialUpdate({
      uuid: template.value.uuid,
      requestBody: {
        ...values,
        html: template.value.html,
        form_uuids: selectedFormIds.value,
        questionnaire_uuids: selectedQuestionnaireUuids.value
      }
    })
  }

  const saveTemplateContent = async () => {
    if (!template.value) return

    await EmailTemplatesService.emailTemplatesPartialUpdate({
      uuid: template.value.uuid,
      requestBody: {
        html: template.value.html,
        form_uuids: selectedFormIds.value,
        questionnaire_uuids: selectedQuestionnaireUuids.value
      }
    })
  }

  const debouncedSaveContent = debounce(saveTemplateContent, 1000)

  // Watch for changes in rich text editor and sections
  watch([() => template.value?.html, selectedFormIds, selectedQuestionnaireUuids], () => {
    debouncedSaveContent()
  })

  const openPreviewModal = () => {
    showPreviewModal.value = true
  }
</script>
<template>
  <SettingsPage v-if="template && user">
    <template #breadcrumbs-end>
      <li class="inline-flex items-center truncate text-sm font-semibold text-gray-800">
        <SlashIcon class="size-5 shrink-0 text-gray-500" />
        {{ template.name }}
      </li>
    </template>
    <template #actions>
      <div class="flex items-center space-x-2">
        <button
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
            font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:pointer-events-none disabled:opacity-50"
          @click="openPreviewModal"
        >
          <EyeIcon class="h-5 w-5" />
          Vorschau
        </button>
      </div>
    </template>
    <AutoSavingForm @save="handleFormSave">
      <div class="mb-4">
        <InputField
          v-model="template.name"
          name="name"
          label="Name"
          :rules="yup.string().required()"
        />
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <SelectField
            v-model="template.type"
            :options="typeOptions"
            name="type"
            label="Typ"
          />
          <SelectField
            v-model="template.law_field"
            :options="lawFieldOptions"
            name="law_field"
            title="Rechtsgebiet"
            label="Rechtsgebiet"
            :allow-unselect="true"
            unselected-option-text="Alle Rechtsgebiete"
          />
        </div>
        <InputField
          v-model="template.subject"
          name="subject"
          label="Betreff"
          :rules="yup.string().required()"
        />
      </div>
    </AutoSavingForm>
    <div v-if="!isLoading">
      <RichTextEditor
        v-model="template.html"
        :enable-placeholder-button="true"
        :enabled-context-aware-placeholders="['questionnaires_and_documents']"
        :font-color="user.org.email_text_color"
        :font-size="user.org.email_text_size"
        :font-family="user.org.email_text_font"
      />
      <QuestionnaireTemplateSection
        :questionnaires="allQuestionnaires"
        :selected-questionnaire-uuids="selectedQuestionnaireUuids"
        :current-page="questionnairesPage"
        :total-items="totalQuestionnaires"
        :items-per-page="questionnairesPerPage"
        @update:selected-questionnaires="handleSelectedQuestionnairesUpdate"
        @page-change="handleQuestionnairesPageChange"
        @search-change="handleQuestionnairesSearch"
      />
      <DocumentTemplateSection
        :forms="allForms"
        :selected-form-ids="selectedFormIds"
        :current-page="formsPage"
        :total-items="totalForms"
        :items-per-page="formsPerPage"
        @page-change="handleFormsPageChange"
        @update:selected-documents="handleSelectedFormsUpdate"
        @search-change="handleFormsSearch"
      />
    </div>
    <div
      v-else
      class="flex justify-center py-8"
    >
      <div
        class="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"
      ></div>
    </div>
    <EmailPreviewModal
      v-if="showPreviewModal"
      :template-html="template?.html || ''"
      :subject="template?.subject || ''"
      :from-email="user.org.email || ''"
      @close="showPreviewModal = false"
    />
  </SettingsPage>
</template>
